import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Bell, Check, CheckCheck, Trash2, MessageCircle } from "lucide-react";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";

/**
 * Bell-icon notification center for signed-in users.
 * - Lists last 30 notifications (newest first).
 * - Clicking a notification marks it read and navigates to its `link`.
 * - "Mark all read" + per-item delete.
 * - Polls the unread-count endpoint every 60s so badge stays fresh without sockets.
 */
export default function NotificationsBell() {
  const { isRTL, lang } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Lightweight unread-count poll (covers the navbar badge whether or not the dropdown is open)
  const { data: countData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: endpoints.unreadNotificationsCount,
    enabled: !!user,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Full list — only fetched when the popover is open (or just after a mutation)
  const { data: full, isLoading } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => endpoints.listNotifications(30),
    enabled: !!user && open,
  });
  const items = full?.items || [];
  const unread = countData?.unread_count ?? full?.unread_count ?? 0;

  const markRead = useMutation({
    mutationFn: (id) => endpoints.markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
  const markAll = useMutation({
    mutationFn: endpoints.markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
  const removeOne = useMutation({
    mutationFn: (id) => endpoints.deleteNotification(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const goTo = (n) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const formatWhen = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return isRTL ? "الآن" : "now";
    if (diff < 3600) return isRTL ? `${Math.floor(diff / 60)} د` : `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return isRTL ? `${Math.floor(diff / 3600)} س` : `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-full hover:bg-[#2A2421]/5 transition-colors"
        data-testid="notif-bell-btn"
        title={isRTL ? "الإشعارات" : "Notifications"}
        aria-label={isRTL ? "الإشعارات" : "Notifications"}
      >
        <Bell className="w-4 h-4 text-[#2A2421]" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C86240] text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notif-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute ${isRTL ? "left-0" : "right-0"} top-full mt-2 w-[340px] max-w-[92vw] bg-white border border-[#2A2421]/10 rounded-2xl shadow-xl z-50 overflow-hidden`}
          data-testid="notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2A2421]/8 bg-[#F9F6F0]">
            <div className="text-sm font-semibold text-[#2A2421] flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5 text-[#C86240]" />
              {isRTL ? "الإشعارات" : "Notifications"}
              {unread > 0 && <span className="text-[10px] text-[#C86240]">({unread})</span>}
            </div>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                className="text-[11px] text-[#C86240] hover:underline inline-flex items-center gap-1"
                data-testid="notif-mark-all-read"
              >
                <CheckCheck className="w-3 h-3" />
                {isRTL ? "تعليم الكل كمقروء" : "Mark all read"}
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {isLoading && (
              <div className="p-6 text-center text-xs text-[#5C544F]">…</div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="p-8 text-center text-sm text-[#5C544F]">
                {isRTL ? "لا توجد إشعارات بعد." : "No notifications yet."}
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                className={`group flex items-start gap-3 p-3 border-b border-[#2A2421]/5 hover:bg-[#F9F6F0] cursor-pointer transition-colors ${n.read ? "opacity-70" : "bg-[#FFF4EE]/40"}`}
                onClick={() => goTo(n)}
                role="button"
                data-testid={`notif-item-${n.id}`}
              >
                {n.actor_picture ? (
                  <img src={n.actor_picture} alt="" className="w-9 h-9 rounded-full border border-[#2A2421]/10 shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#C86240]/15 flex items-center justify-center text-[#C86240] shrink-0">
                    <MessageCircle className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#2A2421] leading-snug">
                    <span className={`${!n.read ? "font-semibold" : ""}`}>
                      {lang === "ar" ? n.title_ar : n.title_en}
                    </span>
                  </div>
                  {n.body && (
                    <div className="text-[11px] text-[#5C544F] mt-0.5 line-clamp-2 leading-snug">
                      {n.body}
                    </div>
                  )}
                  <div className="text-[10px] text-[#5C544F] mt-1">{formatWhen(n.created_at)}</div>
                </div>
                <div className="flex flex-col items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead.mutate(n.id); }}
                      className="text-[#5C544F] hover:text-[#C86240] p-0.5"
                      title={isRTL ? "تعليم كمقروء" : "Mark as read"}
                      data-testid={`notif-mark-read-${n.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeOne.mutate(n.id); }}
                    className="text-[#5C544F] hover:text-red-600 p-0.5"
                    title={isRTL ? "حذف" : "Delete"}
                    data-testid={`notif-delete-${n.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
